import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Inbox, Mail, ArrowLeft, Plus, Trash2, ImagePlus, Link2, Newspaper, Clapperboard } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const resizeImage = async (file) => {
  let bitmapSource;
  try {
    bitmapSource = await createImageBitmap(file);
  } catch {
    bitmapSource = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("unsupported"));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error("read-failed"));
      reader.readAsDataURL(file);
    });
  }
  const srcW = bitmapSource.width;
  const srcH = bitmapSource.height;
  if (!srcW || !srcH) throw new Error("unsupported");
  for (const [maxW, quality] of [[1200, 0.8], [900, 0.65], [700, 0.5]]) {
    const scale = Math.min(1, maxW / srcW);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(srcW * scale));
    canvas.height = Math.max(1, Math.round(srcH * scale));
    canvas.getContext("2d").drawImage(bitmapSource, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    if (dataUrl.length < 900_000) return dataUrl;
  }
  throw new Error("too-large");
};

const VideosManager = ({ token, onSessionExpired }) => {
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({ title: "", youtube_url: "", prompt: "" });
  const [editingId, setEditingId] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [existingPdf, setExistingPdf] = useState(null);
  const [removePdf, setRemovePdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const pickPdf = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      setError("Only PDF files are supported.");
      return;
    }
    if (file.size > 7 * 1024 * 1024) {
      setError("PDF too large. Please keep it under 7MB.");
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = (ev) => setPdf({ data: ev.target.result, name: file.name });
    reader.readAsDataURL(file);
  };

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/videos`);
      setVideos(data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    const missing = [];
    if (!form.title.trim()) missing.push("title");
    if (!form.youtube_url.trim()) missing.push("YouTube link");
    if (missing.length) {
      setError(`Please add: ${missing.join(", ")}.`);
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        prompt: form.prompt.trim() || null,
        pdf: pdf?.data || null,
        pdf_name: pdf?.name || null,
        remove_pdf: removePdf && !pdf,
      };
      if (editingId) {
        await axios.put(`${API}/videos/${editingId}`, body, auth);
        toast.success("Video updated.");
      } else {
        await axios.post(`${API}/videos`, body, auth);
        toast.success("Video added. It's live on nayanverma.com/content now.");
      }
      setForm({ title: "", youtube_url: "", prompt: "" });
      setEditingId(null);
      setPdf(null);
      setExistingPdf(null);
      setRemovePdf(false);
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Your session expired. Please log in again.");
        onSessionExpired();
        return;
      }
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({ title: v.title, youtube_url: v.youtube_url, prompt: v.prompt || "" });
    setPdf(null);
    setExistingPdf(v.pdf_name || null);
    setRemovePdf(false);
    setError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ title: "", youtube_url: "", prompt: "" });
    setPdf(null);
    setExistingPdf(null);
    setRemovePdf(false);
    setError("");
  };

  const remove = async (video) => {
    try {
      await axios.delete(`${API}/videos/${video.id}`, auth);
      toast.success("Video removed.");
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
    } catch {
      toast.error("Could not remove it. Please try again.");
    }
  };

  return (
    <div className="mt-20" data-testid="videos-manager">
      <p className="font-label text-xs uppercase tracking-[0.3em] text-[#C4A27A] mb-3">Content Page</p>
      <h2 className="font-display text-3xl sm:text-4xl tracking-tighter">
        AI Videos<span className="text-bronze">.</span>
      </h2>
      <p className="text-[#8A8A93] text-sm mt-3 mb-10 max-w-md">
        These appear on your public page at <span className="text-[#C4A27A]">/content</span> — share
        that link on Instagram. Visitors watch the video and download/copy the prompt.
      </p>

      <form onSubmit={add} className="border border-white/10 bg-[#0A0A0A] p-8 space-y-6" data-testid="video-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <input className="input-luxe" placeholder="Video title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="video-title-input" />
          <input className="input-luxe" placeholder="YouTube link (video or Shorts)" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} data-testid="video-url-input" />
        </div>
        <textarea
          className="input-luxe resize-none"
          rows={4}
          placeholder="The prompt behind this video (visitors can download/copy it — hidden if you attach a PDF)"
          value={form.prompt}
          onChange={(e) => setForm({ ...form, prompt: e.target.value })}
          data-testid="video-prompt-input"
        />
        <div className="flex flex-wrap items-center gap-4">
          <label className="btn-luxe inline-flex items-center gap-2 font-label text-xs uppercase tracking-[0.2em] border border-white/20 text-[#F3F3F1] px-5 py-3 cursor-pointer">
            <ImagePlus size={14} /> {pdf || (existingPdf && !removePdf) ? "Replace PDF" : "Attach PDF (optional)"}
            <input type="file" accept="application/pdf" className="hidden" onChange={pickPdf} data-testid="video-pdf-input" />
          </label>
          {pdf && (
            <span className="inline-flex items-center gap-2 text-emerald-400 text-xs" data-testid="video-pdf-ready">
              {pdf.name} ✓
              <button type="button" onClick={() => setPdf(null)} className="text-[#8A8A93] hover:text-red-400" aria-label="Remove chosen PDF">×</button>
            </span>
          )}
          {!pdf && existingPdf && !removePdf && (
            <span className="inline-flex items-center gap-2 text-[#C4A27A] text-xs" data-testid="video-pdf-existing">
              Current: {existingPdf}
              <button type="button" onClick={() => setRemovePdf(true)} className="text-[#8A8A93] hover:text-red-400" aria-label="Remove PDF" data-testid="video-pdf-remove">× remove</button>
            </span>
          )}
          {removePdf && !pdf && <span className="text-red-400 text-xs">PDF will be removed on save</span>}
          <span className="text-[#8A8A93] text-xs">When a PDF is attached, visitors download it and the prompt is hidden.</span>
        </div>
        {error && (
          <p className="text-red-400 text-sm border border-red-400/30 bg-red-400/5 px-4 py-3" data-testid="video-form-error">{error}</p>
        )}
        <button
          type="submit"
          disabled={saving}
          className="btn-luxe inline-flex items-center gap-2 font-label text-xs uppercase tracking-[0.2em] bg-[#C4A27A] text-[#050505] px-6 py-3 disabled:opacity-50"
          data-testid="video-add-button"
        >
          <Plus size={14} /> {saving ? "Saving…" : editingId ? "Save changes" : "Add video"}
        </button>
        {editingId && (
          <button
            type="button"
            onClick={cancelEdit}
            className="btn-luxe ml-3 font-label text-xs uppercase tracking-[0.2em] border border-white/20 text-[#F3F3F1] px-6 py-3"
            data-testid="video-cancel-edit-button"
          >
            Cancel
          </button>
        )}
      </form>

      <div className="grid grid-cols-1 gap-6 mt-8">
        {videos.map((v, i) => (
          <div key={v.id} className="border border-white/10 bg-[#0A0A0A] p-5 flex items-center gap-5" data-testid={`admin-video-${i}`}>
            <span className="w-11 h-11 border border-white/10 flex items-center justify-center shrink-0">
              <Clapperboard size={16} className="text-[#C4A27A]" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg truncate">{v.title}</p>
              <a href={v.youtube_url} target="_blank" rel="noopener noreferrer" className="text-[#C4A27A] text-xs truncate block hover:text-[#E0C39A] transition-colors duration-300">{v.youtube_url}</a>
              {v.prompt && <p className="text-[#8A8A93] text-xs mt-1 line-clamp-1">{v.prompt}</p>}
            </div>
            <button
              onClick={() => startEdit(v)}
              className="btn-luxe font-label text-[10px] uppercase tracking-[0.2em] border border-[#C4A27A]/50 text-[#C4A27A] px-4 py-2 shrink-0"
              data-testid={`video-edit-${i}`}
            >
              Edit
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/content/${v.id}`);
                toast.success("Post link copied — share it with your audience.");
              }}
              className="btn-luxe font-label text-[10px] uppercase tracking-[0.2em] border border-white/20 text-[#F3F3F1] px-4 py-2 shrink-0"
              data-testid={`video-share-link-${i}`}
            >
              Copy post link
            </button>
            <button onClick={() => remove(v)} aria-label={`Delete ${v.title}`} className="text-[#8A8A93] hover:text-red-400 transition-colors duration-300 shrink-0" data-testid={`video-delete-${i}`}>
              <Trash2 size={17} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const PublicationsManager = ({ token, onSessionExpired }) => {
  const [pubs, setPubs] = useState([]);
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [links, setLinks] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [extraUrl, setExtraUrl] = useState({});

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/publications`);
      setPubs(data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handle401 = (err) => {
    if (err.response?.status === 401) {
      toast.error("Your session expired. Please log in again.");
      onSessionExpired();
      return true;
    }
    return false;
  };

  const linkFromDomain = (raw) => {
    let u = raw.trim();
    if (!/^https?:\/\//.test(u)) u = `https://${u}`;
    try {
      const domain = new URL(u).hostname;
      return {
        url: u.split("?")[0].replace(/\/$/, ""),
        source: domain.replace("www.", "").split(".")[0].replace(/^\w/, (c) => c.toUpperCase()),
        logo: `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
      };
    } catch {
      return null;
    }
  };

  const fetchPreview = async () => {
    if (!url.trim()) {
      setError("Paste the publication link first.");
      return;
    }
    setError("");
    setFetching(true);
    try {
      const { data } = await axios.post(`${API}/publications/preview`, { url }, auth);
      if (links.some((l) => l.url === data.url)) {
        setError("That link is already added below.");
      } else {
        setLinks((prev) => [...prev, { url: data.url, source: data.source, logo: data.logo }]);
        if (!title) setTitle(data.title);
      }
      setUrl("");
    } catch (err) {
      if (handle401(err)) return;
      const fallback = linkFromDomain(url);
      if (fallback && !links.some((l) => l.url === fallback.url)) {
        setLinks((prev) => [...prev, fallback]);
        setUrl("");
        setError("That site blocks auto-reading, so I added it using its domain — edit the outlet name below and type the title yourself if needed.");
      } else {
        const detail = err.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Could not fetch that link. Check the URL and try again.");
      }
    } finally {
      setFetching(false);
    }
  };

  const publish = async () => {
    if (!links.length || !title.trim()) {
      setError("Fetch at least one link and keep a title.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/publications`, { title: title.trim(), text: text.trim() || null, links }, auth);
      toast.success("Published. It's live on your site now.");
      setUrl("");
      setText("");
      setTitle("");
      setLinks([]);
      load();
    } catch (err) {
      if (handle401(err)) return;
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not publish. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const addLinkToExisting = async (pub) => {
    const u = (extraUrl[pub.id] || "").trim();
    if (!u) return;
    try {
      let link;
      try {
        const { data: prev } = await axios.post(`${API}/publications/preview`, { url: u }, auth);
        link = { url: prev.url, source: prev.source, logo: prev.logo };
      } catch (err) {
        if (handle401(err)) return;
        link = linkFromDomain(u);
        if (!link) throw err;
      }
      await axios.post(`${API}/publications/${pub.id}/links`, link, auth);
      toast.success(`${link.source} added to "${pub.title.slice(0, 40)}…"`);
      setExtraUrl((p) => ({ ...p, [pub.id]: "" }));
      load();
    } catch (err) {
      if (handle401(err)) return;
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add that link.");
    }
  };

  const remove = async (pub) => {
    try {
      await axios.delete(`${API}/publications/${pub.id}`, auth);
      toast.success("Publication removed.");
      setPubs((prev) => prev.filter((p) => p.id !== pub.id));
    } catch (err) {
      if (handle401(err)) return;
      toast.error("Could not remove it. Please try again.");
    }
  };

  return (
    <div className="mt-20" data-testid="publications-manager">
      <p className="font-label text-xs uppercase tracking-[0.3em] text-[#C4A27A] mb-3">Showcase</p>
      <h2 className="font-display text-3xl sm:text-4xl tracking-tighter">
        Publications<span className="text-bronze">.</span>
      </h2>
      <p className="text-[#8A8A93] text-sm mt-3 mb-10 max-w-md">
        Paste an article link — the headline and the publication's logo are pulled automatically.
        Fetch more links to attach other outlets to the same article. Duplicate links are rejected.
      </p>

      <div className="border border-white/10 bg-[#0A0A0A] p-8 space-y-6" data-testid="publication-form">
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            className="input-luxe flex-1"
            placeholder="Paste the publication link (https://…)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="publication-url-input"
          />
          <button
            onClick={fetchPreview}
            disabled={fetching}
            className="btn-luxe inline-flex items-center justify-center gap-2 font-label text-xs uppercase tracking-[0.2em] border border-white/20 text-[#F3F3F1] px-6 py-3 disabled:opacity-50 shrink-0"
            data-testid="publication-fetch-button"
          >
            <Link2 size={14} /> {fetching ? "Fetching…" : links.length ? "Fetch another link" : "Fetch link"}
          </button>
        </div>

        {error && (
          <p className="text-red-400 text-sm border border-red-400/30 bg-red-400/5 px-4 py-3" data-testid="publication-form-error">
            {error}
          </p>
        )}

        {links.length > 0 && (
          <div className="border border-[#C4A27A]/30 p-6 space-y-5" data-testid="publication-preview">
            <div className="flex flex-wrap gap-2">
              {links.map((l, j) => (
                <span key={l.url} className="inline-flex items-center gap-2 rounded-full border border-white/20 pl-1.5 pr-3 py-1.5 text-sm text-[#F3F3F1]" data-testid={`publication-link-chip-${j}`}>
                  <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center overflow-hidden">
                    {l.logo ? <img src={l.logo} alt="" className="w-4 h-4 object-contain" /> : <Newspaper size={11} className="text-black" />}
                  </span>
                  <input
                    className="bg-transparent outline-none border-b border-transparent focus:border-[#C4A27A] w-28"
                    value={l.source}
                    onChange={(e) => setLinks(links.map((x) => (x.url === l.url ? { ...x, source: e.target.value } : x)))}
                    data-testid={`publication-source-input-${j}`}
                  />
                  <button onClick={() => setLinks(links.filter((x) => x.url !== l.url))} className="text-[#8A8A93] hover:text-red-400 ml-1" aria-label={`Remove ${l.source}`}>×</button>
                </span>
              ))}
            </div>
            <div>
              <p className="font-label text-[10px] uppercase tracking-[0.25em] text-[#C4A27A] mb-2">Complete article title (editable)</p>
              <textarea
                className="input-luxe resize-none font-medium"
                rows={2}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="publication-title-input"
              />
            </div>
            <textarea
              className="input-luxe resize-none"
              rows={3}
              maxLength={300}
              placeholder="Your text for this publication (optional) — shown under the headline"
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="publication-text-input"
            />
            <button
              onClick={publish}
              disabled={saving}
              className="btn-luxe inline-flex items-center gap-2 font-label text-xs uppercase tracking-[0.2em] bg-[#C4A27A] text-[#050505] px-6 py-3 disabled:opacity-50"
              data-testid="publication-publish-button"
            >
              <Plus size={14} /> {saving ? "Publishing…" : "Publish"}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 mt-8">
        {pubs.map((pub, i) => (
          <div key={pub.id} className="border border-white/10 bg-[#0A0A0A] p-6 space-y-4" data-testid={`admin-publication-${i}`}>
            <div className="flex items-start gap-5">
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg leading-snug">{pub.title}</p>
                {pub.text && <p className="text-[#8A8A93] text-xs mt-1 line-clamp-2">{pub.text}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                  {(pub.links || []).map((l) => (
                    <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/20 pl-1.5 pr-3 py-1 text-xs text-[#C4A27A] hover:border-[#C4A27A] transition-colors duration-300">
                      <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center overflow-hidden">
                        {l.logo ? <img src={l.logo} alt="" className="w-3.5 h-3.5 object-contain" /> : <Newspaper size={10} className="text-black" />}
                      </span>
                      {l.source}
                    </a>
                  ))}
                </div>
              </div>
              <button onClick={() => remove(pub)} aria-label={`Delete ${pub.title}`} className="text-[#8A8A93] hover:text-red-400 transition-colors duration-300 shrink-0" data-testid={`publication-delete-${i}`}>
                <Trash2 size={17} />
              </button>
            </div>
            <div className="flex gap-3">
              <input
                className="input-luxe flex-1 text-sm"
                placeholder="Add another outlet's link for this same article…"
                value={extraUrl[pub.id] || ""}
                onChange={(e) => setExtraUrl((p) => ({ ...p, [pub.id]: e.target.value }))}
                data-testid={`publication-extra-url-${i}`}
              />
              <button
                onClick={() => addLinkToExisting(pub)}
                className="btn-luxe font-label text-[10px] uppercase tracking-[0.2em] border border-white/20 text-[#F3F3F1] px-4 py-2 shrink-0"
                data-testid={`publication-add-link-${i}`}
              >
                Add link
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ShowcaseManager = ({ token, endpoint, title, blurb, prefix, onSessionExpired }) => {
  const [apps, setApps] = useState([]);
  const [form, setForm] = useState({ name: "", url: "", tagline: "" });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/${endpoint}`);
      setApps(data);
    } catch {}
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    try {
      setImage(await resizeImage(file));
    } catch {
      setImage(null);
      setError("That image couldn't be read. Please use a JPG, PNG or WebP file (HEIC photos from iPhone may not work — convert or screenshot it first).");
    }
    e.target.value = "";
  };

  const addApp = async (e) => {
    e.preventDefault();
    setError("");
    const missing = [];
    if (!form.name) missing.push("name");
    if (!form.url) missing.push("website link");
    if (!image) missing.push("image");
    if (missing.length) {
      setError(`Please add: ${missing.join(", ")}.`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/${endpoint}`, { ...form, image }, auth);
      toast.success(`${form.name} added. It's live on your site now.`);
      setForm({ name: "", url: "", tagline: "" });
      setImage(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Your session expired. Please log in again.");
        onSessionExpired();
        return;
      }
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not save. Please check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const removeApp = async (app) => {
    try {
      await axios.delete(`${API}/${endpoint}/${app.id}`, auth);
      toast.success(`${app.name} removed.`);
      setApps((prev) => prev.filter((a) => a.id !== app.id));
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Your session expired. Please log in again.");
        onSessionExpired();
        return;
      }
      toast.error("Could not remove it. Please try again.");
    }
  };

  return (
    <div className="mt-20" data-testid={`${prefix === "business" ? "businesses" : "ai-apps"}-manager`}>
      <p className="font-label text-xs uppercase tracking-[0.3em] text-[#C4A27A] mb-3">Showcase</p>
      <h2 className="font-display text-3xl sm:text-4xl tracking-tighter">
        {title}<span className="text-bronze">.</span>
      </h2>
      <p className="text-[#8A8A93] text-sm mt-3 mb-10 max-w-md">{blurb}</p>

      <form onSubmit={addApp} className="border border-white/10 bg-[#0A0A0A] p-8 space-y-6" data-testid={`${prefix}-form`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <input className="input-luxe" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${prefix}-name-input`} />
          <input className="input-luxe" placeholder="Website link (https://…)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid={`${prefix}-url-input`} />
        </div>
        <input className="input-luxe" placeholder="One-line tagline (optional)" maxLength={120} value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid={`${prefix}-tagline-input`} />
        {error && (
          <p className="text-red-400 text-sm border border-red-400/30 bg-red-400/5 px-4 py-3" data-testid={`${prefix}-form-error`}>
            {error}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-4">
          <label className="btn-luxe inline-flex items-center gap-2 font-label text-xs uppercase tracking-[0.2em] border border-white/20 text-[#F3F3F1] px-5 py-3 cursor-pointer">
            <ImagePlus size={14} /> {image ? "Change image" : "Choose image"}
            <input type="file" accept="image/png, image/jpeg, image/webp, image/*" className="hidden" onChange={pickImage} data-testid={`${prefix}-image-input`} />
          </label>
          {image && <img src={image} alt="preview" className="h-16 w-24 object-contain bg-black border border-white/10" data-testid={`${prefix}-image-preview`} />}
          {image && <span className="text-emerald-400 text-xs" data-testid={`${prefix}-image-ready`}>Image ready ✓</span>}
          <button
            type="submit"
            disabled={saving}
            className="btn-luxe inline-flex items-center gap-2 font-label text-xs uppercase tracking-[0.2em] bg-[#C4A27A] text-[#050505] px-6 py-3 disabled:opacity-50 ml-auto"
            data-testid={`${prefix}-add-button`}
          >
            <Plus size={14} /> {saving ? "Adding…" : "Add"}
          </button>
        </div>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
        {apps.map((app, i) => (
          <div key={app.id} className="border border-white/10 bg-[#0A0A0A] p-5 flex items-center gap-5" data-testid={`admin-${prefix}-${i}`}>
            <img src={app.image} alt={app.name} className="h-16 w-24 object-contain bg-black border border-white/10 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-display text-xl truncate">{app.name}</p>
              {app.tagline && <p className="text-[#8A8A93] text-xs truncate">{app.tagline}</p>}
              <a href={app.url} target="_blank" rel="noopener noreferrer" className="text-[#C4A27A] text-xs truncate block hover:text-[#E0C39A] transition-colors duration-300">
                {app.url}
              </a>
            </div>
            <button onClick={() => removeApp(app)} aria-label={`Delete ${app.name}`} className="text-[#8A8A93] hover:text-red-400 transition-colors duration-300 shrink-0" data-testid={`${prefix}-delete-${i}`}>
              <Trash2 size={17} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const LoginGate = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", data.token);
      onLogin(data.token);
      toast.success("Welcome back, Nayan.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md border border-white/10 p-10 bg-[#0A0A0A]">
        <div className="w-12 h-12 border border-[#C4A27A]/40 flex items-center justify-center mb-8">
          <Lock size={18} className="text-[#C4A27A]" />
        </div>
        <h1 className="font-display text-3xl tracking-tight">Private Study</h1>
        <p className="text-[#8A8A93] text-sm mt-3 leading-relaxed">
          This room is for Nayan only. Enter the password to read your inquiries.
        </p>
        <form onSubmit={submit} className="mt-10 space-y-8">
          <input
            type="password"
            className="input-luxe"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="btn-luxe w-full font-label text-xs uppercase tracking-[0.2em] bg-[#C4A27A] text-[#050505] px-8 py-4 disabled:opacity-50"
            data-testid="admin-login-button"
          >
            {loading ? "Checking…" : "Enter"}
          </button>
        </form>
        <Link to="/" className="inline-flex items-center gap-2 mt-8 text-[#8A8A93] text-xs font-label uppercase tracking-[0.2em] hover:text-[#F3F3F1] transition-colors duration-300" data-testid="admin-back-home-link">
          <ArrowLeft size={12} /> Back to site
        </Link>
      </div>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [inquiries, setInquiries] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/inquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(data);
    } catch {
      toast.error("Session expired. Please log in again.");
      onLogout();
    }
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen max-w-5xl mx-auto px-6 lg:px-8 py-16" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-label text-xs uppercase tracking-[0.3em] text-[#C4A27A] mb-3">Private Study</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">
            Inquiries<span className="text-bronze">.</span>
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="font-label text-xs uppercase tracking-[0.2em] text-[#8A8A93] hover:text-[#F3F3F1] transition-colors duration-300" data-testid="admin-view-site-link">
            View Site
          </Link>
          <button
            onClick={onLogout}
            className="btn-luxe inline-flex items-center gap-2 font-label text-xs uppercase tracking-[0.2em] border border-white/20 text-[#F3F3F1] px-5 py-3"
            data-testid="admin-logout-button"
          >
            <LogOut size={13} /> Log Out
          </button>
        </div>
      </div>
      <p className="text-[#8A8A93] text-sm mb-12" data-testid="admin-inquiry-count">
        {inquiries === null ? "Reading the letters…" : `${inquiries.length} message${inquiries.length === 1 ? "" : "s"} waiting for your attention.`}
      </p>

      {inquiries !== null && inquiries.length === 0 && (
        <div className="border border-white/10 p-16 text-center" data-testid="admin-empty-state">
          <Inbox size={28} className="text-[#C4A27A] mx-auto mb-6" />
          <p className="font-display text-2xl italic text-[#8A8A93]">Silence, for now.</p>
        </div>
      )}

      <div className="space-y-6">
        {(inquiries || []).map((q, i) => (
          <article key={q.id} className="border border-white/10 bg-[#0A0A0A] p-8" data-testid={`inquiry-item-${i}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-2xl tracking-tight">{q.name}</h2>
                <a href={`mailto:${q.email}`} className="inline-flex items-center gap-2 text-[#C4A27A] text-sm mt-1 hover:text-[#E0C39A] transition-colors duration-300">
                  <Mail size={13} /> {q.email}
                </a>
              </div>
              <span className="font-label text-[10px] uppercase tracking-[0.25em] text-[#8A8A93]">{formatDate(q.created_at)}</span>
            </div>
            {q.subject && (
              <p className="font-label text-xs uppercase tracking-[0.2em] text-[#F3F3F1] mt-6">{q.subject}</p>
            )}
            <p className="text-[#8A8A93] leading-relaxed mt-4 whitespace-pre-wrap">{q.message}</p>
          </article>
        ))}
      </div>

      <ShowcaseManager
        token={token}
        endpoint="businesses"
        title="Businesses"
        blurb='Businesses you add here appear in the "Businesses" section on your site. Remove one and it disappears instantly.'
        prefix="business"
        onSessionExpired={onLogout}
      />
      <ShowcaseManager
        token={token}
        endpoint="ai-apps"
        title="AI Apps"
        blurb='Apps you add here appear in the "AI Apps" section on your site. Remove one and it disappears instantly.'
        prefix="ai-app"
        onSessionExpired={onLogout}
      />
      <PublicationsManager token={token} onSessionExpired={onLogout} />
      <VideosManager token={token} onSessionExpired={onLogout} />
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));

  const logout = useCallback(() => {
    localStorage.removeItem("admin_token");
    setToken(null);
  }, []);

  return (
    <div className="bg-[#050505] text-[#F3F3F1] min-h-screen">
      {token ? <Dashboard token={token} onLogout={logout} /> : <LoginGate onLogin={setToken} />}
    </div>
  );
}
